/* eslint-disable indent */
import { LearnerSelfReviews } from '../../../models/learner-skill-resource';
import { RatingScale } from '../../../models/rating-scale';
import { ReducerAction } from '../../../models/reducer-state';
import { ReviewResource } from '../../../models/review-resource';
import { TeamMemberReviewResource } from '../../../models/team-member-review-resource';
import { LearnerCareerPathSkillSet } from '../../../models/view/learner-career-path-skill-set';
import { RatingPageTypes } from './rating.models';
import { PagePath } from '../../../navigation/navigation.enums';
import { Breadcrumb } from '@keplerco/core';
import { generateLPIText } from '../career-path/career-path.helpers';

// state
interface RatingState {
  breadcrumbs: Breadcrumb[];
  title: string;
  subtitle: string;

  type?: RatingPageTypes;
  ratingScale?: RatingScale;
  learnerSelfReviews?: LearnerSelfReviews;
  teamMember?: TeamMemberReviewResource;
  careerPathSkillSet?: LearnerCareerPathSkillSet;
}

export const initialState: RatingState = {
  title: 'Rating',
  breadcrumbs: [],
  subtitle: '',
};

// actions
export enum RatingActionTypes {
  PopulateSelf = 'populate-self',
  PopulatePeer = 'populate-peer',
  PopulateCareerPath = 'populate-career-path',
  PatchSelfSkill = 'patch-learner-skill',
  PatchPeerSkill = 'patch-team-member-skill',
  PatchCareerPathSkill = 'patch-career-path-skill',
}

type RatingActions =
  | ReducerAction<RatingActionTypes.PopulateSelf, { payload: { basePagePath: string; forcedAssessmentsLength: number; assessmentName: string; ratingScale: RatingScale | undefined; learnerSelfReviews: LearnerSelfReviews | undefined } }>
  | ReducerAction<RatingActionTypes.PopulatePeer, { payload: { basePagePath: string; forcedAssessmentsLength: number; assessmentName: string; ratingScale: RatingScale | undefined; teamMember: TeamMemberReviewResource | undefined; peerOnly: boolean } }>
  | ReducerAction<RatingActionTypes.PopulateCareerPath, { payload: { subdomain: string; basePagePath: string; forcedAssessmentsLength: number; assessmentName: string; ratingScale: RatingScale | undefined; careerPathSkillSet: LearnerCareerPathSkillSet | undefined } }>
  | ReducerAction<RatingActionTypes.PatchSelfSkill, { payload: { skillId: number; skillLevel: number; yearsSpent: number; comment: string } }>
  | ReducerAction<RatingActionTypes.PatchPeerSkill, { payload: { skillId: number; skillLevel: number | undefined; comment: string } }>
  | ReducerAction<RatingActionTypes.PatchCareerPathSkill, { payload: { skillId: number; skillLevel: number } }>;

// reducer
export function reducer(state: RatingState, action: RatingActions): RatingState {
  if (action.type === RatingActionTypes.PopulateSelf) {
    return {
      ...state,
      breadcrumbs: action.payload.forcedAssessmentsLength > 1
        ? [
            {
              name: 'Pending assessments',
              url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
            },
            {
              name: action.payload.assessmentName,
              url: action.payload.basePagePath,
            },
            {
              name: 'Frequency of use',
              url: action.payload.basePagePath + PagePath.analysisFrequencyOfUse,
            },
            {
              name: 'Self assessment',
              url: location.pathname,
            },
          ]
        : [
            {
              name: action.payload.assessmentName,
              url: action.payload.basePagePath,
            },
            {
              name: 'Frequency of use',
              url: action.payload.basePagePath + PagePath.analysisFrequencyOfUse,
            },
            {
              name: 'Self assessment',
              url: location.pathname,
            },
          ],
      title: 'Self assessment',
      subtitle: 'Rate your level of expertise in each of the skills below, as well as how many years you have been using the skill',
      type: 'self',
      ratingScale: action.payload.ratingScale,
      learnerSelfReviews: action.payload.learnerSelfReviews,
    };
  }

  if (action.type === RatingActionTypes.PopulatePeer) {
    const breadcrumbs = action.payload.forcedAssessmentsLength > 1
      ? [
          {
            name: 'Pending assessments',
            url: PagePath.analysisBase,
          },
          {
            name: action.payload.assessmentName,
            url: action.payload.basePagePath,
          },
        ]
      : [
          {
            name: action.payload.assessmentName,
            url: action.payload.basePagePath,
          },
        ];

    if (!action.payload.peerOnly) {
      breadcrumbs.push(
        {
          name: 'Frequency of use',
          url: action.payload.basePagePath + PagePath.analysisFrequencyOfUse,
        },
        {
          name: 'Self assessment',
          url: action.payload.basePagePath + PagePath.analysisSelfAssessment,
        },
        {
          name: `Rate your peers' skills`,
          url: action.payload.basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisRateYourPeersSkills,
        },
        {
          name: 'Peer skills assessment',
          url: action.payload.basePagePath + PagePath.analysisSelfAssessment + PagePath.analysisPeerSkillsAssessment,
        }
      );
    } else {
      breadcrumbs.push(
        {
          name: `Rate your peers' skills`,
          url: action.payload.basePagePath + PagePath.analysisRateYourPeersSkills,
        },
        {
          name: 'Peer skills assessment',
          url: action.payload.basePagePath + PagePath.analysisPeerSkillsAssessment,
        }
      );
    }

    breadcrumbs.push({
      name: !!action.payload.teamMember ? `${action.payload.teamMember.firstName} ${action.payload.teamMember.lastName}` : 'Your peer' + `'s skill rating`,
      url: location.pathname,
    });

    return {
      ...state,
      breadcrumbs,
      title: !!action.payload.teamMember ? `${action.payload.teamMember.firstName} ${action.payload.teamMember.lastName}` : 'Your peer' + `'s skill rating`,
      subtitle: `Assess ${!!action.payload.teamMember ? (action.payload.teamMember.firstName.endsWith('s') ? `${action.payload.teamMember.firstName}'` : `${action.payload.teamMember.firstName}'s`) : `your peer's`} level of expertise in each of the skills below`,
      type: 'peer',
      ratingScale: action.payload.ratingScale,
      teamMember: action.payload.teamMember,
    };
  }

  if (action.type === RatingActionTypes.PopulateCareerPath) {
    return {
      ...state,
      breadcrumbs: action.payload.forcedAssessmentsLength > 1
        ? [
            {
              name: 'Pending assessments',
              url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
            },
            {
              name: action.payload.assessmentName,
              url: action.payload.basePagePath,
            },
            {
              name: `Choose your ${generateLPIText(action.payload.subdomain)}s`,
              url: action.payload.basePagePath + PagePath.analysisChooseYourCareerPaths,
            },
            {
              name: action.payload.subdomain.includes('lpi') ? 'Capability map' : 'Rate your skills',
              url: action.payload.basePagePath + PagePath.analysisRateYourSkills,
            },
            {
              name: action.payload.careerPathSkillSet?.skillSetName ?? 'Rating',
              url: location.pathname,
            },
          ]
        : [
            {
              name: action.payload.assessmentName,
              url: action.payload.basePagePath,
            },
            {
              name: `Choose your ${generateLPIText(action.payload.subdomain)}s`,
              url: action.payload.basePagePath + PagePath.analysisChooseYourCareerPaths,
            },
            {
              name: action.payload.subdomain.includes('lpi') ? 'Capability map' : 'Rate your skills',
              url: action.payload.basePagePath + PagePath.analysisRateYourSkills,
            },
            {
              name: action.payload.careerPathSkillSet?.skillSetName ?? 'Rating',
              url: location.pathname,
            },
          ],
      title: action.payload.careerPathSkillSet?.skillSetName ?? 'Rating',
      subtitle: 'Rate your level of expertise in each of the skills below',
      type: 'career-path',
      ratingScale: action.payload.ratingScale,
      careerPathSkillSet: action.payload.careerPathSkillSet,
    };
  }

  if (action.type === RatingActionTypes.PatchSelfSkill) {
    if (!state.learnerSelfReviews) return state;

    const reviews = state.learnerSelfReviews.reviews.map(review => {
      if (review.companySkillId === action.payload.skillId) {
        return { ...review, skillLevel: action.payload.skillLevel, yearsSpent: action.payload.yearsSpent, comment: action.payload.comment };
      }
      return { ...review };
    });

    return { ...state, learnerSelfReviews: { ...state.learnerSelfReviews, reviews } };
  }

  if (action.type === RatingActionTypes.PatchPeerSkill) {
    if (!state.teamMember) return state;
    
    const reviews: ReviewResource[] = state.teamMember.reviews.map(review => {
      if (review.companySkillId === action.payload.skillId) {
        return { ...review, skillLevel: action.payload.skillLevel, comment: action.payload.comment };
      }
      return { ...review };
    });

    return { ...state, teamMember: { ...state.teamMember, reviews } };
  }

  if (action.type === RatingActionTypes.PatchCareerPathSkill) {
    if (!state.careerPathSkillSet) return state;

    const reviews: ReviewResource[] = state.careerPathSkillSet.reviews.map(review => {
      if (review.companySkillId === action.payload.skillId) {
        return { ...review, skillLevel: action.payload.skillLevel };
      }
      return review;
    });

    return { ...state, careerPathSkillSet: { ...state.careerPathSkillSet, reviews } };
  }

  return state;
}
